import {AxiosError, HttpStatusCode} from "axios";
import {StateConfig} from "@/api/dto/state-config";
import {Address, EncounterType, PatientPhoneNumber} from "@/api/dto/client-profile";
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import Helpers from "@/helpers/helpers";
import {differenceInCalendarDays, isValid, parse, startOfDay} from "date-fns";

const DisplayHelpers = {
  displayHonorific(honorific: string | undefined) {
    if(!honorific) return '';
    if(['MD'].includes(honorific)) {
      return 'Dr.';
    } else {
      return honorific;
    }
  },
  formatDateTime(date: string, seconds?: boolean) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString(
      'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        ...(seconds && { second: '2-digit' }),
      }
    );
  },
  formatDateTimeShort(date: string) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    })+ ' at ' + dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  },
  formatDateTimeLong(date: string, includeTimezone: boolean = false) {
    const dateObj = new Date(date);
    const datePart = dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timePart = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    if (!includeTimezone) {
      return `${datePart} at ${timePart}`;
    }
    const timeZone = dateObj.toString().match(/\(([^)]+)\)/)?.[1]
      ?.split(' ')
      .map(w => w[0])
      .join('') ?? '';
    return `${datePart} at ${timePart} ${timeZone}`.trim();
  },
  formatTime(date: string) {
    const dateObj = new Date(date);
    return dateObj.toLocaleTimeString(
      'en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  },
  formatDate(date: string | null | undefined | Date) {
    if (!date) return '';
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  },
  // A due date is a calendar date, not a point in time, but the API carries it as an instant.
  // Reading one with new Date() and rendering it in the viewer's zone is what made the same
  // due date show as 04/04 in EDT and 04/03 in CDT (CARECO-4182). The calendar day is already
  // the date part of the string, so take it verbatim and never let a zone touch it.
  calendarDatePart(value: string | Date | null | undefined): string {
    if (!value) return '';

    if (value instanceof Date) {
      if (!isValid(value)) return '';
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return value.trim().split('T')[0] ?? '';
  },
  // Render a calendar date as MM/DD/YYYY. Every viewer sees the same day regardless of zone.
  formatCalendarDate(value: string | Date | null | undefined): string {
    const [year, month, day] = this.calendarDatePart(value).split('-');
    if (!year || !month || !day) return '';
    return `${month}/${day}/${year}`;
  },
  // Parse a calendar date to local midnight, for same-day comparisons such as overdue.
  parseCalendarDay(value: string | Date | null | undefined): Date | null {
    const datePart = this.calendarDatePart(value);
    if (!datePart) return null;

    const parsed = parse(datePart, 'yyyy-MM-dd', new Date());
    return isValid(parsed) ? startOfDay(parsed) : null;
  },
  // Parse a date-only string such as 2000-01-01 to a Date object
  parseDateOnly(date: string, formatString : string = 'yyyy-MM-dd') {
    return parse(date, formatString, new Date());
  },
  normalizeDateValue(value: unknown): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }

    if (typeof value === "string") {
      const parsedDate = this.parseDateOnly(value.slice(0, 10));
      return isValid(parsedDate) ? parsedDate : null;
    }

    return null;
  },

  daysSince(date: string | Date | null | undefined): number {
    const parsedDate = this.normalizeDateValue(date);
    return parsedDate ? differenceInCalendarDays(new Date(), parsedDate) : 0;
  },
  formatTimeUTC(date: string) {
    return this.formatTime(this.getDateWithTimezone(date).toString());
  },
  formatDateUTC(date: string) {
    if (!date) return '';
    return this.formatDate(this.getDateWithTimezone(date).toString());
  },
  formatDateFromDateOnlyString(date: string | null) {
    if (!date) return '';

    const dateObj = new Date(date + 'T00:00:00'); // needed to make javascript parse as local time instead of UTC
    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  },
  formatTimeSince(unit: string, value: number): string {
    return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
  },
  timeSince(date: Date, short?: boolean): string {
    const now = new Date();
    const secondsPast = (now.getTime() - date.getTime()) / 1000;

    if (secondsPast < 60) {
      return this.formatTimeSince("second", Math.round(secondsPast));
    }
    if (secondsPast < 3600) {
      const unit = short ? "min" : "minute";
      return this.formatTimeSince(unit, Math.floor(secondsPast / 60));
    }
    if (secondsPast < 86400) {
      return this.formatTimeSince("hour", Math.floor(secondsPast / 3600));
    }
    if (secondsPast < 2592000) {
      return this.formatTimeSince("day", Math.floor(secondsPast / 86400));
    }
    if (secondsPast < 31536000) {
      return this.formatTimeSince("month", Math.floor(secondsPast / 2592000));
    }
    return this.formatTimeSince("year", Math.floor(secondsPast / 31536000));
  },
  getDateMonthDay(date: string | Date) {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit'
    });
  },
  getDateWithTimezone(date: string) {
    const dateObj = new Date(date);

    const timeInSeconds = dateObj.getTime() / 1000;
    const timezoneOffsetInSeconds = dateObj.getTimezoneOffset() * 60;

    return new Date((timeInSeconds - timezoneOffsetInSeconds) * 1000);
  },
  formatDateOfBirthWithAge(dateOfBirthString: string | undefined): string {
    if (!dateOfBirthString) return '';
    const dob = new Date(dateOfBirthString);

    const now = Date.now();
    const diff = new Date(now.valueOf() - dob.valueOf());
    const age = Math.max(0, diff.getUTCFullYear() - 1970);

    const formattedDate = this.formatDateFromDateOnlyString(dateOfBirthString);

    return `${formattedDate} (${age} yo)`;
  },
  parseDate(date: string | number | undefined, fallback: Date): Date {
    const parsedDate = new Date(date || "");
    return !parsedDate || parsedDate.toString() == "Invalid Date" ? fallback : parsedDate;
  },
  formatName(firstName: string | undefined, middleName: Nullable<string> | undefined, lastName: string | undefined) {
    let formattedName = "";

    if (firstName) {
      formattedName += this.capitalizeFirstLetter(firstName);
    }

    if (middleName) {
      formattedName += " " + this.capitalizeFirstLetter(middleName);
    }

    if (lastName) {
      formattedName += " " + this.capitalizeFirstLetter(lastName);
    }

    return formattedName.trim();
  },
  capitalizeFirstLetter(name: string) {
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  },
  formatEmptySpace(email:string){
    if (!email) return '';
    return email.replace(/\s+/g, '');
  },
  removeUnnecessaryWhitespace(value: string | null) {
    return value?.replace(/\s\s+/g, ' ').trim() ?? '';
  },
  // Drops a leading US country-code '1' from an 11-digit number (CARECO-6168); US-only.
  normalizeUsPhoneDigits(raw: string | null | undefined): string {
    const digits = (raw ?? '').replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  },
  formatPhoneNumber(phoneNumber: string | null | undefined) {
    // this display helper should be used for form inputs
    // phone data we control or can verify vailidity
    // (ie user phone numbers, facility phone numbers)
    if (!phoneNumber) return '';

    const phone = this.normalizeUsPhoneDigits(phoneNumber).substring(0, 10);
    const areaCode = phone.substring(0, 3);
    const middle = phone.substring(3, 6);
    const last = phone.substring(6, 10);

    let formattedPhone = '';
    if (phone.length > 6) {
      formattedPhone = `(${areaCode}) ${middle}-${last}`;
    } else if (phone.length > 3) {
      formattedPhone = `(${areaCode}) ${middle}`;
    } else if (phone.length > 0) {
      formattedPhone = `${areaCode}`;
    }

    return formattedPhone;
  },
  formatE164PhoneNumber(phoneNumber: string | null | undefined): string | null {
    if (!phoneNumber) return null;
    const parsed = parsePhoneNumberFromString(phoneNumber, 'US');
    // Using isPossible() intentionally to be lenient with dirty patient data
    // that may not pass strict isValid() checks against known numbering plans.
    if (parsed && parsed.isPossible()) {
      return parsed.format('E.164');
    }
    return null;
  },
  formatPatientPhoneNumber(patientPhoneNumber: PatientPhoneNumber | undefined) {
    const phoneNumber = patientPhoneNumber?.phone;
    if (!phoneNumber) return '';

    const scrubbedPhone = parsePhoneNumberFromString(phoneNumber, 'US');
    if (scrubbedPhone && scrubbedPhone.isPossible()) {
      return scrubbedPhone.formatNational();
    }
    return phoneNumber;
  },
  cleanGroupTypeKey(groupType: string): string | null {
    if (groupType === 'OWNING_ORG') {
      return null;
    }
    return groupType.replace(/_/g, ' ');
  },
  formatAddress(address: Address | null | undefined): string {
    if (!address) {
      return '';
    }

    const { address1, address2,city, state, zip } = address;
    const streetParts = [address1, address2].filter(Boolean).join(', ');
    const parts = [
      streetParts,
      city,
      state && zip ? `${state} ${zip}` : state || zip
    ];
    return Helpers.joinWithComma(...parts.filter(Boolean));
  },
  formatSSN(ssn: string | null | undefined) {
    if (!ssn) {
      return '';
    }

    const cleaned = ssn.replace(/\D/g,'');

    if (cleaned.length === 9) {
      return `xxx-xx-${cleaned.slice(5)}`;
    }

    if (cleaned.length === 4) {
      return `xxx-xx-${cleaned}`;
    }

    return 'could not format: ' + ssn;
  },
  genderFromEnum(genderEnum: string | undefined) {
    switch (genderEnum) {
    case "M": {
      return "Male";
    }
    case "F": {
      return "Female";
    }
    case "U": {
      return "Unknown";
    }
    case "O": {
      return "Other";
    }
    case "X": {
      return "Non-binary";
    }
    default: {
      return "Unknown";
    }
    }
  },
  humanize(str: string | null | undefined) {
    if (!str) return '';

    return str
      .trim()
      .split(/\s+/)
      .map((str) => str
        .replace(/([a-z])([A-Z][a-z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/\s+/g, ' ')
        .trim())
      .join(' ')
      .toLowerCase()
      .replace(/^./, (str) => str.toUpperCase());
  },
  toTitlecase(str: string | null | undefined): string {
    if (!str) return '';

    const lowerCaseWords = new Set(['or', 'and', 'the', 'a', 'an', 'of', 'in', 'on', 'at', 'for', 'to', 'with', 'by', 'but']);

    return str
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((word, index) => {
        if (index !== 0 && lowerCaseWords.has(word)) {
          return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  },

  displayErrorMessage(error: Error | null) {
    if (!error) return '';

    if(error instanceof AxiosError) {
      if(error.response?.status === HttpStatusCode.Forbidden) {
        return 'You do not have permission to view this resource. ' +
          'If you believe you are seeing this message in error please contact your account manager.';
      }
    }

    return error.message;
  },
  contactAccountManagerMessage() {
    return 'To change your platform functionality, contact your account manager.';
  },
  contactAdminMessage() {
    return 'To change your platform functionality, contact your account admin.';
  },
  contactGroupAdminMessage() {
    return 'To update your group permissions, contact your admin.';
  },
  contactAdminAccessMessage() {
    return 'To change your platform functionality, contact your admin.';
  },
  notAvailableInStateMessage() {
    return 'Not available in your state. Please reach out to your Account Management team for more information.';
  },
  readableEncounterType(type: EncounterType, stateConfig: StateConfig | undefined) {
    switch(type) {
    case EncounterType.ADT:
      return 'ADT';
    case EncounterType.OPENBEDS_REFERRAL:
      return stateConfig?.branding.openBeds.referral.event;
    case EncounterType.FINDHELP_SDOH:
      return stateConfig?.branding.findHelp.event;
    case EncounterType.JUSTICE_INVOLVED:
      return 'Justice Involved';
    case EncounterType.OPENBEDS_CRISIS:
      return stateConfig?.branding.openBeds.crisis.event;
    }
  },
  aiAgentUser: {
    id: 'ai-agent',
    first_name: 'Automated',
    last_name: 'Agent',
    image: '/favicon.png',
  },
};

export default DisplayHelpers;
